import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import Translate from 'ui/atoms/translate';
import { toNumber } from 'ui/helper/money';
import PlaceholderFallback from 'ui/atoms/placeholder-fallback';
import Date from 'ui/atoms/date';
import DateAtom from 'ui/atoms/date';
import {
  IssuerInvestmentStatusDescription,
  IssuerInvestmentStatusIndicator,
} from 'src/apps/issuer/shared/issuer-investment-status';
import Currency from 'ui/atoms/currency';
import {
  AdminApi,
  AdminInvestmentDetails,
  CancellationImpedimentsEnum,
  CountryEnum,
  FrontendStatusEnum,
  InvestmentAcquisitionTypeRecipientEnum,
  InvestmentCancellationEnum,
  Money,
} from 'api';
import Button from 'ui/atoms/button';
import EditPaymentDateModal from 'apps/issuer/pages/investment-details/general-information/edit-payment-date-modal';
import CopyButton from 'ui/molecules/copy-button';
import Hint from 'ui/atoms/hint';
import ActionButtons from 'ui/molecules/action-buttons';
import InvestmentCancellationModal, { CancellationModalType } from 'apps/issuer/shared/investment-cancellation-modal';
import useApiCall from 'hooks/use-api-call';
import Spacer from 'ui/atoms/spacer';
import Tooltip from 'ui/atoms/tooltip';
import { pascalCase } from 'change-case';
import CardItem from 'src/ui/atoms/card-item';

export interface GeneralInvestmentDetailsProps {
  /** Primary content. */
  investorName?: string;
  investmentId: string;
  signedAt: Date;
  acceptedDate?: Date | null;
  paymentDate?: Date | null;
  investorCountry?: CountryEnum;
  status?: FrontendStatusEnum;
  entryType?: string;
  transferReference?: string;
  transactionUrl?: string;
  /** Additional classes. */
  className?: string;
  investmentTotal: Money;
  investmentTotalFulfilled?: Money;
  tokenPrice?: Money;
  remainingAmount: number;
  acquisitionType?: InvestmentAcquisitionTypeRecipientEnum;
  setInvestment: (investment: AdminInvestmentDetails) => void;
  canCancelReasons: InvestmentCancellationEnum[];
  cancellationImpediments: CancellationImpedimentsEnum[];
  cancellationDeadline: Date | null;
  cancellationDate: Date | null;
  loadData: () => void;
  hasManageDataPermission: boolean;
  distributionPlatformId: string;
  hasFrontend: boolean;
}

// TODO(geforcefan): remove minimumTicketSizeAlternative translation, params needed for rule types
const GeneralInvestmentDetails: FunctionComponent<GeneralInvestmentDetailsProps> = (props) => {
  const {
    investorName,
    investmentId,
    acceptedDate,
    signedAt,
    paymentDate,
    status,
    entryType,
    transactionUrl,
    investmentTotal,
    investmentTotalFulfilled,
    tokenPrice,
    remainingAmount,
    acquisitionType,
    setInvestment,
    canCancelReasons,
    cancellationImpediments,
    cancellationDeadline,
    cancellationDate,
    loadData,
    hasManageDataPermission,
    distributionPlatformId,
    hasFrontend,
  } = props;

  const [cancellationModalOpen, setCancellationModalOpen] = useState(false);
  const [editPaymentDetailsOpen, setEditPaymentDetailsOpen] = useState<boolean>(false);
  const [errorCancellation, setErrorCancellation] = useState(false);
  const [successCancellation, setSuccessCancellation] = useState(false);
  const [distributionPlatform, setDistributionPlatform] = useState('');

  const { withApi, makeAuthenticatedApi, loading } = useApiCall();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const getDistributionPlatformName = useCallback(() => {
    if (!distributionPlatformId || distributionPlatformId?.length === 0) return;

    withApi(async () => {
      try {
        const { displayName } = await adminApi.adminDistributionPlatformsRetrieve({ id: distributionPlatformId });
        setDistributionPlatform(displayName);
      } catch (e) {
        console.error(e);
      }
    });
  }, [adminApi, distributionPlatformId]);

  useEffect(() => {
    getDistributionPlatformName();
  }, [withApi, adminApi, distributionPlatformId]);

  const hasInvestmentAmountChanged = (): boolean => {
    return toNumber(investmentTotal) !== toNumber(investmentTotalFulfilled);
  };

  const canCancelInvestment: boolean = useMemo(
    () => canCancelReasons.includes(InvestmentCancellationEnum.CANCELLATION) && hasManageDataPermission,
    [JSON.stringify(canCancelReasons), hasManageDataPermission],
  );

  const hasCancellationImpediments: boolean = useMemo(
    () => !!cancellationImpediments.length && hasManageDataPermission,
    [JSON.stringify(cancellationImpediments), hasManageDataPermission],
  );

  const onCancelInvestment = useCallback(() => {
    if (!canCancelReasons?.length) return;
    const cancelReason = canCancelReasons.includes(InvestmentCancellationEnum.REJECTION)
      ? InvestmentCancellationEnum.REJECTION
      : InvestmentCancellationEnum.CANCELLATION;

    withApi(async () => {
      try {
        await adminApi.adminInvestmentsCancellationCreate({
          id: investmentId,
          investmentCancellationRequest: {
            reason: cancelReason,
          },
        });
        setSuccessCancellation(true);
        loadData();
      } catch (e) {
        setErrorCancellation(true);
        console.error(e);
      }
    });
  }, [investmentId, JSON.stringify(canCancelReasons)]);

  useEffect(() => {
    if (cancellationModalOpen) {
      setSuccessCancellation(false);
      setErrorCancellation(false);
    }
  }, [cancellationModalOpen]);

  return (
    <>
      <CardItem
        title={<Translate name="generalInvestmentDetails.investmentId" />}
        content={investmentId}
        icon={<CopyButton valueToCopy={investmentId} />}
      />
      <CardItem
        title={<Translate name="generalInvestmentDetails.investorName" truncate={true} />}
        content={investorName}
      />
      <CardItem
        title={<Translate name="generalInvestmentDetails.investmentTotal" truncate={true} />}
        content={
          <>
            {investmentTotal && <Currency>{investmentTotal}</Currency>}
            {investmentTotalFulfilled && hasInvestmentAmountChanged() && (
              <>
                {' ('}
                <Currency>{investmentTotalFulfilled}</Currency>
                {')'}
              </>
            )}
          </>
        }
      />
      {tokenPrice && (
        <CardItem
          title={<Translate name="generalInvestmentDetails.unitPrice" truncate={true} />}
          content={<Currency decimals={'*'}>{tokenPrice}</Currency>}
        />
      )}
      <CardItem title={<Translate name="generalInvestmentDetails.unitNumber" />} content={remainingAmount} />
      <CardItem title={<Translate name="entryType.label" />} content={<Translate name={`entryType.${entryType}`} />} />
      {distributionPlatform.length > 0 && (
        <CardItem
          title={<Translate name="generalInvestmentDetails.distributionPlatform" />}
          content={distributionPlatform}
        />
      )}
      <CardItem
        title={<Translate name="generalInvestmentDetails.signedAt" truncate={true} />}
        content={<Date>{signedAt}</Date>}
      />
      <CardItem
        title={<Translate name="generalInvestmentDetails.acceptedDate" truncate={true} />}
        content={<PlaceholderFallback>{acceptedDate && <Date>{acceptedDate}</Date>}</PlaceholderFallback>}
      />
      <CardItem
        title={<Translate name="generalInvestmentDetails.paymentDate" truncate={true} />}
        content={
          <PlaceholderFallback>
            {paymentDate && (
              <>
                <Date>{paymentDate}</Date>
              </>
            )}
          </PlaceholderFallback>
        }
        icon={
          paymentDate &&
          hasManageDataPermission &&
          !cancellationDate && (
            <Button
              icon="edit"
              color="primary"
              variant="link"
              onClick={() => setEditPaymentDetailsOpen(!editPaymentDetailsOpen)}
              inline
            />
          )
        }
      />
      {status && (
        <CardItem
          title={<Translate name="generalInvestmentDetails.status" truncate={true} />}
          content={<IssuerInvestmentStatusIndicator status={status} />}
        />
      )}

      {status && (
        <>
          <Spacer y={4} />
          <IssuerInvestmentStatusDescription
            status={status}
            transactionUrl={transactionUrl}
            signedAt={acquisitionType === InvestmentAcquisitionTypeRecipientEnum.HANDOVER ? signedAt : null}
            investmentTotalFulfilled={
              status === FrontendStatusEnum.SUCCESS_PARTIALLY_TRANSFERED ? investmentTotalFulfilled : null
            }
          />
        </>
      )}
      {canCancelInvestment && cancellationDeadline && (
        <Hint variant="warning">
          <Translate
            name="generalInvestmentDetails.cancellationDate"
            args={[<DateAtom>{cancellationDeadline}</DateAtom>]}
          />
        </Hint>
      )}
      {canCancelInvestment && !hasCancellationImpediments && (
        <ActionButtons spacingTop="regular">
          <Button variant="secondary" color="danger" onClick={() => setCancellationModalOpen(true)}>
            <Translate name="generalInvestmentDetails.cancelInvestmentButton" />
          </Button>
        </ActionButtons>
      )}
      {hasCancellationImpediments && (
        <ActionButtons spacingTop="regular">
          <Tooltip
            maxWidth={188}
            content={
              <Translate
                name={`generalInvestmentDetails.cancellationImpediment${pascalCase(cancellationImpediments[0])}`}
              />
            }
          >
            <Button variant="secondary" disabled>
              <Translate name={'generalInvestmentDetails.cancelInvestmentButton'} />
            </Button>
          </Tooltip>
        </ActionButtons>
      )}
      {cancellationModalOpen && (
        <InvestmentCancellationModal
          onCancelInvestment={onCancelInvestment}
          loading={loading}
          onClose={() => setCancellationModalOpen(false)}
          successCancellation={successCancellation}
          errorCancellation={errorCancellation}
          hasFrontend={hasFrontend}
          type={
            canCancelReasons.includes(InvestmentCancellationEnum.REJECTION)
              ? CancellationModalType.REJECTION
              : CancellationModalType.CANCELLATION
          }
        />
      )}
      {editPaymentDetailsOpen && (
        <EditPaymentDateModal
          investmentId={investmentId}
          acceptedDate={acceptedDate}
          paymentDate={paymentDate}
          setInvestment={setInvestment}
          onHideModal={() => setEditPaymentDetailsOpen(false)}
        />
      )}
    </>
  );
};

export default GeneralInvestmentDetails;
